import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';

interface EditAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (appointmentData: any) => void;
  appointment: any;
}

const EditAppointmentDialog: React.FC<EditAppointmentDialogProps> = ({
  open,
  onClose,
  onSave,
  appointment,
}) => {
  const [formData, setFormData] = useState({
    status: '',
    notes: '',
    appointmentDate: null as Date | null,
    startTime: null as Date | null,
  });

  useEffect(() => {
    if (appointment) {
      setFormData({
        status: appointment.status || '',
        notes: appointment.notes || '',
        appointmentDate: appointment.appointmentDate ? new Date(appointment.appointmentDate) : null,
        startTime: appointment.startTime ? (() => {
          const [hours, minutes] = appointment.startTime.split(':');
          const time = new Date();
          time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          return time;
        })() : null,
      });
    }
  }, [appointment]);

  const handleSave = () => {
    const updateData = {
      id: appointment.id,
      status: formData.status,
      notes: formData.notes,
      appointmentDate: formData.appointmentDate?.toISOString().split('T')[0],
      startTime: formData.startTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      totalDuration: appointment.totalDuration,
    };
    
    onSave(updateData);
    onClose();
  };

  const statusOptions = [
    { value: 'SCHEDULED', label: 'Scheduled' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'NO_SHOW', label: 'No Show' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Appointment
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Client: {appointment?.client?.firstName} {appointment?.client?.lastName}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Staff: {appointment?.primaryStaff?.firstName} {appointment?.primaryStaff?.lastName}
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 3 }}>
            Total: ${appointment?.totalPrice}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Appointment Date"
                value={formData.appointmentDate}
                onChange={(date) => setFormData({ ...formData, appointmentDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TimePicker
                label="Start Time"
                value={formData.startTime}
                onChange={(time) => setFormData({ ...formData, startTime: time })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAppointmentDialog;
